import { useMemo } from 'react';
import { cn } from '@divlab/divanui';
import { IconInfoPopover, IconDeleteLarge } from '@divlab/divanui/icons';

import { useChangeKitQuantity } from '@Queries/Kit';
import useTranslation from '@Queries/useTranslation';
import { useDeps } from '@Contexts/DI';
import * as ModalWindows from '@Stores/ModalWindows';
import ProductPreview from '@Components/ProductPreview';
import ProductNoImage from '@Components/ProductNoImage';
import styles from './Product.module.css';

import type { FC, HTMLAttributes, MouseEvent } from 'react';
import type { ProductData } from '@Types/Product';

interface ProductProps extends HTMLAttributes<HTMLDivElement> {
  product: ProductData;
  isUnavailable: boolean;
  mainProductId: number;
  onClick?: (e: MouseEvent) => void;
}

const Product: FC<ProductProps> = (props) => {
  const { product, isUnavailable, onClick, mainProductId, className, ...restProps } = props;
  const { mutate: changeQuantity } = useChangeKitQuantity(mainProductId);
  const { t } = useTranslation();
  const { name, images } = product;
  const [firstImage] = images || [];
  const wordsName = name.split(' ');
  const { analytics } = useDeps();

  const sizes: string = useMemo(() => {
    const result = [];

    product.parameterGroups.forEach((parameterGroup) => {
      if (parameterGroup.theme !== 'sizes') return;

      const parameters = product.parameters.filter((targetParameter) => {
        return targetParameter.groupId === parameterGroup.id;
      });

      parameters.forEach((parameter) => {
        const parameterValue = product.parameterValues.find((pv) => {
          return pv.parameterId === parameter.id;
        });
        const unit = product.units.find((u) => u.id === parameterValue.unitId);

        result.push({
          value: parameterValue.value,
          unit: unit.title,
        });
      });
    });

    if (result.length < 1) return null;

    const value = result.map((size) => size.value).join(' х ');
    return `${value} ${result[0].unit}`;
  }, [product]);

  function handleClickDelete(e: MouseEvent) {
    e.stopPropagation();

    changeQuantity({ product, quantity: 0 });

    analytics.dispatchEvent('kits.removeProduct', { type: product.type });

    if (onClick) onClick(e);
  }

  function handleClickItem(e: MouseEvent) {
    e.stopPropagation();

    if (isUnavailable) return;

    ModalWindows.open('KitProductInfo', { product, mainProductId });
  }

  return (
    <div
      {...restProps}
      className={cn(styles.main, { [styles.transparent]: isUnavailable }, className)}
      onClick={handleClickItem}
    >
      {firstImage ? (
        <ProductPreview
          image={firstImage}
          className={styles.imageWrapper}
          cnImage={styles.image}
          orientation='landscape'
        />
      ) : (
        <ProductNoImage
          className={cn(styles.imageWrapper, styles.noImage)}
          orientation='landscape'
        />
      )}
      <div className={styles.content}>
        <div className={styles.characteristic}>
          <h2 className={styles.titleWrapper}>
            <span>{wordsName.slice(0, wordsName.length - 1).join(' ')}</span>
            <span className={styles.sizeWrapper}>
              {wordsName[wordsName.length - 1]}
              <IconInfoPopover className={styles.icon} />
            </span>
          </h2>
          {sizes && (
            <div className={styles.sizes}>
              <div className={styles.title}>{t('ui.sizes')}:</div>
              <div className={styles.values}>{sizes}</div>
            </div>
          )}
        </div>
        <div className={styles.trash}>
          <IconDeleteLarge onClick={handleClickDelete} />
        </div>
      </div>
    </div>
  );
};

export default Product;
